import React, { useState, useRef, useEffect } from 'react';
import {
    Layout, Card, Button, Spin, Alert, message,
    Typography, Flex
} from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';
import fileDownload from 'js-file-download';
import * as publicActions from 'store/actions/PublicActions';
import useIsMobile from '../../../hooks/useIsMobile';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const { Content, Header } = Layout;
const { Title, Text } = Typography;

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PublicViewerView = ({ document, recipient, token }) => {
    const pdfContainerRef = useRef(null);
    const isMobile = useIsMobile();

    // State
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [, setPdfLoading] = useState(true);
    const [pdfError, setPdfError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Initialize PDF URL and mark as viewed
    useEffect(() => {
        if (document?.signed_document_file) {
            setPdfUrl(document.signed_document_file);
        } else if (document?.document_file_url || document?.document_file) {
            setPdfUrl(document.document_file_url || document.document_file);
        }

        // Mark document as viewed
        if (token) {
            publicActions.markDocumentViewed(token);
        }
    }, [document, token]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
        setPdfError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error("PDF load error:", error);
        setPdfLoading(false);
        setPdfError(error);
        message.error("Failed to load PDF document");
    };

    // Handle download
    const handleDownload = async () => {
        if (!pdfUrl) {
            message.error("No document available for download");
            return;
        }

        setDownloading(true);
        try {
            const response = await axios.get(pdfUrl, {
                responseType: "blob",
            });

            if (response.status === 200) {
                const filename = document?.name || document?.title || 'document.pdf';
                fileDownload(response.data, filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
                message.success("Download started");
            }
        } catch (err) {
            console.error("Download error:", err);
            message.error("Failed to download document. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            {/* Simple Header */}
            <Header style={{
                background: '#fff',
                height: 'auto',
                lineHeight: 1.4,
                padding: isMobile ? '10px 12px' : '12px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,            // correct way to control spacing
                        minWidth: 0,       // let the title ellipsize instead of pushing the button out
                    }}
                >
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
                        <EyeOutlined style={{ marginRight: 8 }} />
                        View Document
                    </Title>

                    <Text type="secondary" style={{ margin: 0 }} ellipsis>
                        {document?.title || document?.name}
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
                    {!isMobile && (
                        <Text>
                            Viewing as: <strong>{recipient?.name}</strong>
                        </Text>
                    )}
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                        loading={downloading}
                    >
                        {isMobile ? 'Download' : 'Download PDF'}
                    </Button>
                </div>
            </Header>

            <Content style={{ padding: 24 }}>
                <Alert
                    message="View-Only Access"
                    description="You have been granted view-only access to this document. You can view and download the document but cannot make any changes."
                    type="info"
                    showIcon
                    icon={<EyeOutlined />}
                    style={{ marginBottom: 16 }}
                />

                {/* PDF Preview */}
                <Card>
                    <div
                        ref={pdfContainerRef}
                        style={{
                            backgroundColor: '#666',
                            padding: 20,
                            borderRadius: 8,
                            maxHeight: 'calc(100vh - 300px)',
                            overflowY: 'auto'
                        }}
                    >
                        <Flex justify="center">
                            {pdfError ? (
                                <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                                    <p>Failed to load PDF</p>
                                </div>
                            ) : pdfUrl ? (
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={<Spin size="large" tip="Loading PDF..." />}
                                >
                                    {numPages > 0 && [...Array(numPages).keys()].map((_, index) => {
                                        const pageNumber = index + 1;
                                        return (
                                            <div key={pageNumber} style={{ marginBottom: 10 }}>
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={zoomLevel}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                                <div style={{
                                                    textAlign: 'center',
                                                    backgroundColor: '#fff',
                                                    padding: '5px'
                                                }}>
                                                    Page {pageNumber} of {numPages}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Document>
                            ) : (
                                <div style={{ color: '#fff', padding: 40 }}>
                                    No PDF preview available
                                </div>
                            )}
                        </Flex>
                    </div>

                    {/* Zoom Controls */}
                    <Flex justify="center" style={{ marginTop: 16 }} gap={16} align="center">
                        <Button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}>
                            Zoom Out
                        </Button>
                        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
                        <Button onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.25))}>
                            Zoom In
                        </Button>
                    </Flex>
                </Card>
            </Content>
        </Layout>
    );
};

export default PublicViewerView;
